import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import api from "../utils/base_url";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Footer, Navbar } from "../components";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart } from "../redux/action";
import { motion } from "framer-motion";
import "../styles/Favorites.css"
import { useCartPricing } from "../hooks/useCartPricing";

const Favorites = () => {
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [stockError, setStockError] = useState({});
    const [disabledQty, setDisabledQty] = useState({});
    const { user } = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [animatingHeart, setAnimatingHeart] = useState(null);
    const state = useSelector((state) => state.handleCart);

    const { pricingMap } = useCartPricing(state);

    const getCartQty = (productId) => {
        const item = state.find((p) => p.id === productId);
        return item ? item.qty : 0;
    };

    const addProduct = (product) => {
        if (!user) {
            toast.error("Please login first!");
            navigate("/login");
            return;
        }

        if (!product.is_available || product.stock_quantity === 0) {
            toast.error("This product is out of stock!");
            return;
        }

        const cartItem = state.find((item) => item.id === product.id);
        const currentQty = cartItem ? cartItem.qty : 0;

        if (currentQty >= product.stock_quantity) {
            setStockError(prev => ({ ...prev, [product.id]: true }));
            setDisabledQty(prev => ({ ...prev, [product.id]: true }));

            setTimeout(() => {
                setStockError(prev => ({ ...prev, [product.id]: false }));
                setDisabledQty(prev => ({ ...prev, [product.id]: false }));
            }, 2000);

            return;
        }


        dispatch(addCart(product));
    };
    const removeItem = (product) => {
        dispatch(delCart(product));
    };

    const fetchFavorites = useCallback(async (pageNo = 1) => {
        if (!user) return;

        setLoading(true);
        try {
            const res = await api.get(`favorites/?auth_id=${user.id}&page=${pageNo}`);
            setFavorites(res.data.data || []);
            setPage(res.data.current_page || 1);
            setTotalPages(res.data.total_pages || 1);
        } catch (err) {
            toast.error("Could not load favorites");
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (!user) {
            navigate("/login");
            return;
        }
        fetchFavorites();
    }, [user, navigate, fetchFavorites]);

    const toggleFavorite = async (productId) => {
        if (!user) {
            toast.error("Please login first!");
            navigate("/login");
            return;
        }

        try {
            const res = await api.post("favorites/toggle/", { product_id: productId, auth_id: user.id });
            const isFav = res.data.favorite;

            setFavorites((prev) =>
                isFav ? [...prev, productId] : prev.filter((id) => id !== productId)
            );

            setAnimatingHeart(productId);

            // Reset animation after 400ms
            setTimeout(() => setAnimatingHeart(null), 400);
            fetchFavorites();
        } catch (err) {
            console.error(err);
            toast.error("Failed, try again");
        }
    };

    const LoadingGrid = () => (
        <div className="grid-container">
            {[...Array(6)].map((_, idx) => (
                <div key={idx} className="skeleton-card">
                    <Skeleton height={280} />
                </div>
            ))}
        </div>
    );

    return (
        <>
            <Navbar />
            <div className="favorites-page">
                <div className="container my-4 py-3">
                    <h2 className="favorites-title display-6 fw-bold text-center text-uppercase mb-4">
                        My Favorites
                    </h2>
                    {loading ? <LoadingGrid /> : (
                        <>
                            <div className="grid-container">
                                {favorites.length === 0 ? (
                                    <div className="empty-wrapper">
                                        <h3 className="empty-text">
                                            No favorite products yet.
                                        </h3>
                                    </div>

                                ) : favorites.map((product, idx) => {
                                    const hasOffer = product.offer_percentage && Number(product.offer_percentage) > 0 && product.offer_price;
                                    const outOfStock = !product.is_available || product.stock_quantity === 0;
                                    const stockLow = product.stock_quantity <= 10 && product.stock_quantity > 0;
                                    const altCardClass = idx % 2 === 0 ? "card-even" : "card-odd";
                                    const p = product ? pricingMap?.[product.id] : null;
                                    const hasWholesale = p && p.discount_percent > 0;

                                    return (
                                        <div key={product.id}
                                            className={`product-card ${altCardClass}${outOfStock ? " out-of-stock" : ""}`}
                                            onClick={() => {
                                                if (!outOfStock) navigate(`/product/${product.id}`);
                                            }}
                                        >
                                            {hasOffer && (
                                                <span className="offer-badge">
                                                    {Math.round(product.offer_percentage)}% OFF
                                                </span>
                                            )}

                                            {outOfStock && (
                                                <span className="stock-badge out-stock">Out of Stock</span>
                                            )}

                                            {!outOfStock && stockLow && (
                                                <span className="stock-badge low-stock">
                                                    Hurry! Only {product.stock_quantity} left
                                                </span>
                                            )}

                                            <div className="img-wrapper">
                                                <img
                                                    src={`${product.product_image}`}
                                                    alt={product.product_name}
                                                />
                                            </div>

                                            <div className="product-content">
                                                <h5 className="product-title">
                                                    {product.product_name.length > 15
                                                        ? product.product_name.substring(0, 15) + "..."
                                                        : product.product_name}
                                                </h5>
                                                <div
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleFavorite(product.id);
                                                    }}
                                                    className="fav-circle-wrap">
                                                    {user ? (
                                                        <div className="fav-circle">
                                                            {favorites.some(
                                                                (fav) => fav.id === product.id
                                                            ) ? (
                                                                <motion.span
                                                                    className="favorite-icon active"
                                                                    animate={
                                                                        animatingHeart === product.id
                                                                            ? {
                                                                                scale: [1, 1.35, 1],
                                                                            }
                                                                            : {}
                                                                    }
                                                                    transition={{
                                                                        duration: 0.3,
                                                                    }}
                                                                >
                                                                    ❤️
                                                                </motion.span>
                                                            ) : (
                                                                <motion.span
                                                                    className="favorite-icon inactive"
                                                                    whileTap={{ scale: 0.9 }}
                                                                >
                                                                    🤍
                                                                </motion.span>
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                {stockError[product.id] && (
                                                    <motion.div
                                                        initial={{ x: 100, opacity: 0 }}
                                                        animate={{
                                                            x: [100, 0, -6, 6, -6, 6, 0],
                                                            opacity: 1,
                                                        }}
                                                        transition={{ duration: 0.6, ease: "easeOut" }}
                                                        exit={{
                                                            x: -100,
                                                            opacity: 0,
                                                            transition: { duration: 0.3 }
                                                        }}
                                                        style={{
                                                            position: "absolute",
                                                            top: "8px",
                                                            left: "33%",
                                                            transform: "translateX(-50%)",
                                                            padding: "6px 14px",
                                                            background: "#dc3545",
                                                            color: "white",
                                                            borderRadius: "20px",
                                                            fontSize: "0.8rem",
                                                            fontWeight: 600,
                                                            zIndex: 99,
                                                            whiteSpace: "nowrap",
                                                            boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                                                        }}
                                                    >
                                                        Stock limit reached!
                                                    </motion.div>
                                                )}
                                                <p className="product-desc">{product.product_description}</p>
                                                <div className="product-price-wrap">
                                                    {hasWholesale ? (
                                                        <>
                                                            <span className="product-price-offer">
                                                                ₹ {Number(p.wholesale_price).toLocaleString()}
                                                            </span>
                                                            <span className="product-price-original">
                                                                <s>₹ {parseFloat(p.original_price).toLocaleString()}</s>
                                                            </span>
                                                        </>
                                                    ) : hasOffer ? (
                                                        <>
                                                            <span className="product-price-offer">
                                                                ₹ {parseFloat(product.offer_price).toLocaleString()}
                                                            </span>
                                                            <span className="product-price-original">
                                                                <s>₹ {parseFloat(product.price).toLocaleString()}</s>
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="product-price">
                                                            ₹ {parseFloat(product.price).toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>
                                                {hasWholesale && (
                                                    <span className="wholesale-badge-mini">{p.discount_percent}% WholeSale off</span>
                                                )}
                                            </div>

                                            <div className="product-btns" onClick={(e) => e.stopPropagation()}>

                                                {outOfStock ? (
                                                    <span className="out-stock-label">Out of Stock</span>
                                                ) : getCartQty(product.id) === 0 ? (
                                                    <button
                                                        className="btn-buy"
                                                        onClick={() => addProduct(product)}
                                                    >
                                                        Add to Cart
                                                    </button>
                                                ) : (
                                                    <div className="qty-box">
                                                        <button
                                                            className="qty-btn"
                                                            onClick={() => removeItem(product)}
                                                            disabled={disabledQty[product.id]}
                                                        >
                                                            <i className="fas fa-minus"></i>
                                                        </button>

                                                        <span className="qty-value">{getCartQty(product.id)}</span>

                                                        <button
                                                            className="qty-btn"
                                                            onClick={() => addProduct(product)}
                                                            disabled={disabledQty[product.id]}
                                                        >
                                                            <i className="fas fa-plus"></i>
                                                        </button>
                                                    </div>


                                                )}

                                            </div>

                                        </div>
                                    );
                                })}
                            </div>

                            {/* ✅ Pagination moved OUT of grid */}
                            <div className="pagination-container">
                                <button
                                    className="btn-nav"
                                    disabled={page === 1}
                                    onClick={() => fetchFavorites(page - 1)}
                                >
                                    ‹ Prev
                                </button>

                                <button
                                    className="btn-nav"
                                    disabled={page === totalPages}
                                    onClick={() => fetchFavorites(page + 1)}
                                >
                                    Next ›
                                </button>
                            </div>
                        </>
                    )}

                </div>
            </div>
            <Footer />

        </>
    );
};

export default Favorites;
